import { authHeader, BASE_URL, handleResponse } from '@helpers/api';
import { LocationIntegrationName } from '@constants/integrations';
import { googleIntegrationService } from './google-integration';
import { facebookIntegrationService } from './fb-integration';
import { coverManagerIntegrationService } from './coverManager-integration';
import { vtexIntegrationService } from './vtex-integration';
import { tiktokIntegrationService } from './tiktok-integration';
import { xIntegrationService } from './x-integration';
import { linkedinIntegrationService } from './linkedin-integration';
import { ProductEvent } from '@constants/product-events';
import { captureOnSuccess } from '@helpers/product-events';

export const connectLocationIntegration = (integrationType, locationId, profile = {}, integrationId = null) => {
    // Fall back to the owner id the selected resource carries (Google/Facebook); single-account types carry none.
    const ownerIntegrationId = integrationId ?? profile?.integrationId ?? null;
    switch (integrationType) {
        case LocationIntegrationName.Google:
            return googleIntegrationService.editProfile(locationId, {
                locationId,
                googlePlaceId: profile.placeId || null,
                googleAccountId: profile.accountId || null,
                googleLocationId: profile.locationId || null,
                name: profile.title,
                integrationId: ownerIntegrationId,
            });
        case LocationIntegrationName.Facebook:
            return facebookIntegrationService.editProfile(locationId, {
                locationId,
                pageId: profile.id,
                name: profile.name,
                username: profile.username,
                integrationId: ownerIntegrationId,
            });
        case LocationIntegrationName.CoverManager:
            return coverManagerIntegrationService.editProfile(locationId, {
                locationId,
                restaurantSlug: profile.slug,
                name: profile.name,
            });
        case LocationIntegrationName.VTEX:
            return vtexIntegrationService.editProfile(locationId, {
                locationId,
                accountName: profile.accountName || null,
            });
        // TikTok/X have no per-resource profile, so the explicit id is the only source (no fallback).
        case LocationIntegrationName.Tiktok:
            return tiktokIntegrationService.connectLocation(locationId, integrationId);
        case LocationIntegrationName.X:
            return xIntegrationService.connectLocation(locationId, integrationId);
        case LocationIntegrationName.LinkedIn:
            return linkedinIntegrationService.connectLocation(locationId, integrationId, profile);
        default:
            return Promise.reject(new Error(`Unsupported location integration type: ${integrationType}`));
    }
};

export const buildConnectMappingOperation = (integrationType, locationId, profile = {}, integrationId = null) => {
    // Mirror connectLocationIntegration's owner fallback so the batch path can't drop the resource's owner id.
    const ownerIntegrationId = integrationId ?? profile?.integrationId ?? null;
    const base = { type: 'Connect', integrationType, locationId, integrationId: ownerIntegrationId };
    switch (integrationType) {
        case LocationIntegrationName.Google:
            return {
                ...base,
                googlePlaceId: profile.placeId || null,
                googleAccountId: profile.accountId || null,
                googleLocationId: profile.locationId || null,
                name: profile.title,
            };
        case LocationIntegrationName.Facebook:
            return { ...base, pageId: profile.id, name: profile.name, username: profile.username };
        case LocationIntegrationName.CoverManager:
            return { ...base, restaurantSlug: profile.slug, name: profile.name };
        case LocationIntegrationName.VTEX:
            return { ...base, accountName: profile.accountName || null };
        case LocationIntegrationName.LinkedIn:
            return { ...base, authorUrn: profile.urn, name: profile.name };
        case LocationIntegrationName.Square:
            return { ...base, squareLocationId: profile.id };
        default:
            return base;
    }
};

export const buildDisconnectMappingOperation = (integrationType, locationId, locationIntegrationId) => ({
    type: 'Disconnect',
    integrationType,
    locationId,
    locationIntegrationId,
});

export const applyLocationIntegrationMappings = operations =>
    fetch(`${BASE_URL}api/locations/integrations/apply`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ operations }),
    })
        .then(handleResponse)
        .then(captureOnSuccess(ProductEvent.IntegrationMappingsApplied, () => ({ operations: operations.length })));
